import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { Subscription } from 'rxjs'
import { $LocalStorage } from '../../../NonReact/utils/StorageUtils'
import { ReactState } from '../Contexts'
import { StateSetter, StateSetterAction } from '../Types/Types'
import { localStorageSet$ } from '../../../NonReact/common/RxJS/LocalStorageState'

export function usePersistentState<T>(key: string, initialValue: T): ReactState<T> {
    const [state, setState]: ReactState<T> = useState<T>((): T => $LocalStorage.getItem<T>(key) ?? initialValue)

    const subscription: MutableRefObject<Nullable<Subscription>> = useRef<Nullable<Subscription>>()

    const setStateAndLocalStorage: StateSetter<T> = useCallback(
        (value: StateSetterAction<T>): void => {
            setState(value)

            if (typeof value === 'function') {
                $LocalStorage.setItem(key, (value as (prevState: T) => T)(state))
            } else {
                $LocalStorage.setItem(key, value)
            }
        },
        [key, state]
    )

    useEffect((): (() => void) => {
        subscription.current?.unsubscribe()
        subscription.current = localStorageSet$.observable.subscribe((localKey: string): void => {
            if (localKey === key) {
                setState($LocalStorage.getItem<T>(key) ?? initialValue)
            }
        })

        return (): void => subscription.current?.unsubscribe()
    }, [key])

    return [state, setStateAndLocalStorage]
}
